'use client'
import create from 'zustand'
import { StateStorage, devtools, persist } from 'zustand/middleware'
import {
  CodeAuthRefreshTokensDocument,
  CodeAuthRefreshTokensMutationVariables,
  EducationLevelType,
  MeDocument,
  MeQuery,
  MeQueryVariables,
  Team,
  TeamEducationLevel,
  // PasswordRecoverMutationVariables,
  // Role,
  UserFieldsFragment,
  useMeLazyQuery,
  // usePasswordRecoverMutation,
} from '@/graphql/generated/graphql'
import { produce } from 'immer'
import { WritableDraft } from 'immer/dist/internal'
import { immer } from 'zustand/middleware/immer'
import { configs } from '@/configs'
import Router from 'next/router'
import { useRouter } from '@/utils/nextjsClient'
import { toast } from '@/services/toastService'
import { useCallback, useEffect } from 'react'
import { accessToken, refreshToken } from './cookies'
import { client } from '@/app/Providers/ApolloContext'
import { PermissionBaseName, userHasPermissionFn } from '@/utils/permissions'
import {
  educationLevelTypeStore,
  selectedEducationLevelTypeFn,
  useSelectedEducationLevelType,
} from '@/app/Providers/EducationLevelsContext/educationLevelState'
import { clearAllStorage } from '@/services/storageCleaner'
import { getLoginOrRegisterUrl } from '@/services/routes'

/**
 * O QUE LEVAR EM CONSIDERAÇÃO
 *
 * Acessando o site pelo URL
 * withREQUEST -> ✅ Access -> ✅
 * withREQUEST -> ❌ Access + ✅ Refresh -> ✅
 * withREQUEST -> ❌ Access + ❌ Refresh -> ❌
 * w/utREQUEST -> ❌ Access + ❌ Refresh -> ❌ (useAuthListener)
 */

// Z is Zustand, to make easy to find
type State = {
  zAccessToken: string | undefined // when stack layers, temp open is turned off and on again
  zRefreshToken: string | undefined // when stack layers, temp open is turned off and on again
  user: UserFieldsFragment | undefined
  /**
   * In case of maintainer admin
   */
  maintainerAdminSelectedTeamId: number | undefined
}

interface Actions {
  setState: (updater: (state: WritableDraft<State>) => void) => void
}

const initialState: State = {
  zAccessToken: undefined,
  zRefreshToken: undefined,
  user: undefined,
  maintainerAdminSelectedTeamId: undefined,
}

// Does not persist isRefreshing to avoid keep refreshing after reload page during refresh
let isRefreshing = false

const authState = create<State & Actions>()(
  devtools(
    persist(
      immer((set, get) => ({
        ...initialState,
        setState: (e) => set(e, undefined, 'setState'),
      })),
      {
        name: 'authPersist',
        // getStorage: () => storage, // canot perform long here
        version: 1,
      },
    ),
    {
      name: 'zAuth',
      serialize: true,
      enabled: configs.IS_DEVELOPMENT,
      anonymousActionType: 'zAuthAnon',
    },
  ),
)

let autoLogoutTimeout: NodeJS.Timeout

export const logout = ({
  redirectPath = getLoginOrRegisterUrl({ isLogout: true, type: 'login' }),
}: { redirectPath?: string } = {}) => {
  authState.setState(initialState, true)
  clearAllStorage()
  client.clearStore()
  if (redirectPath) window.location.href = redirectPath
}

export const useLogout = () => {
  return {
    logout,
  }
}

// const isStaffSelector = (user: UserDataInToken | null | undefined) => {
//   const currentUserRole = user?.role
//   return currentUserRole === Role.Admin || currentUserRole === Role.Support
// }

const parseJwt = (token: string): UserDataInToken | null => {
  const universalAtob = (b64Encoded: string) => {
    try {
      return atob(b64Encoded)
    } catch (err) {
      return Buffer.from(b64Encoded, 'base64').toString()
    }
  }

  if (!token) return null

  try {
    const base64Url = token.split('.')[1]
    if (!base64Url) return null
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      universalAtob(base64)
        .split('')
        .map(function (c) {
          return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`
        })
        .join(''),
    )

    return JSON.parse(jsonPayload)
  } catch (err) {
    return null
  }
}

const handleAuthState = (refreshToken?: string) => {
  clearTimeout(autoLogoutTimeout)

  const refresh = refreshToken || authState.getState().zRefreshToken
  if (refresh) {
    const exp = parseJwt(refresh)?.exp
    if (!exp) return
    const remaining = exp * 1000 - Date.now()

    const TIMEOUT_MAX = 2147483647 // 24 days max https://stackoverflow.com/questions/3468607/why-does-settimeout-break-for-large-millisecond-delay-values
    autoLogoutTimeout = setTimeout(() => {
      logout({})
    }, Math.min(remaining, TIMEOUT_MAX))
  }
}
authState.subscribe((newState, oldState) => {
  handleAuthState(newState.zRefreshToken)

  if (newState.zAccessToken && newState.zRefreshToken) {
    accessToken.write(undefined, newState.zAccessToken)
    refreshToken.write(undefined, newState.zRefreshToken)
  } else {
    accessToken.delete()
    refreshToken.delete()
  }
})

export const addSubscriber = (
  listener: (state: State, prevState: State) => void,
) => {
  if (typeof window === 'undefined') return
  return authState.subscribe((state, prevState) => {
    listener(state, prevState)
  })
}

export const getAuthUser = () => {
  const access = authState.getState().zAccessToken
  if (!access) return undefined
  const user = parseJwt(access)

  return user
}

export const getAuthStateUser = () => {
  return authState.getState().user
}

// export const getIsStaff = () => {
//   const user = getAuthUser()
//   return isStaffSelector(user)
// }

export type AuthenticatedUser = UserDataInToken &
  Partial<UserFieldsFragment> & {
    isAdmin?: boolean
    permissions?: PermissionBaseName[]
    selectedLevel?: EducationLevelType
    hasPermission?: (neededPermission: PermissionBaseName) => boolean
  }
export type AuthUser = AuthenticatedUser | undefined
export const useAuthUser = (): AuthUser => {
  const token = authState((s) => s.zAccessToken)
  const selectedLevelStore = educationLevelTypeStore((s) => s.selectedLevel)

  const userFields = authState((s) => s.user)
  if (!token) return undefined

  const tokenUser = parseJwt(token)

  if (!tokenUser) return undefined

  const isAdmin = userFields?.email?.includes('@ledgertec.com.br') ?? false

  const permissions = (userFields?.permissions ?? []) as PermissionBaseName[]

  const Team = userFields?.Team as Team

  const selectedLevel = selectedEducationLevelTypeFn({
    user: userFields,
    selectedLevel: selectedLevelStore,
  })

  const user = {
    ...tokenUser,
    ...userFields,
    permissions,
    isAdmin,
    Team,
    selectedLevel,
    hasPermission: (neededPermission: PermissionBaseName, pure?: boolean) =>
      userHasPermissionFn({
        userPermissions: permissions,
        neededPermission,
        selectedLevel,
        pure,
      }),
  }

  return user
}

// export const useIsStaff = () => {
//   const user = useAuthUser()
//   return isStaffSelector(user)
// }

interface UserDataInToken extends UserFieldsFragment {
  iat: number
  exp: number
}

export const setUser = ({ user }: { user: UserFieldsFragment }) => {
  authState.setState({
    user,
  })
}

export const updateUserAuthMeNotObservable = async () => {
  const res = await client.mutate<MeQuery, MeQueryVariables>({
    mutation: MeDocument,
    fetchPolicy: 'network-only',
  })

  if (res.data?.me) {
    setUser({ user: res.data.me })
  }
}

export const useAuthMe = () => {
  const [run, data] = useMeLazyQuery()

  const update = useCallback(async () => {
    const res = await run()

    if (res.data?.me) {
      setUser({ user: res.data.me })
    }

    return res
  }, [run])

  return [update, data] as const
}

// export const useAuthChangePassword = () => {
//   const [recover, { loading }] = usePasswordRecoverMutation()

//   const changePassword = async (
//     variables: PasswordRecoverMutationVariables,
//     token: string,
//   ) => {
//     const logged = await recover({
//       variables,
//       context: {
//         headers: {
//           Authorization: `bearer ${token}`,
//         },
//       },
//     })
//     if (logged.data?.passwordRecover) {
//       toast('Senha alterada com sucesso', 'success')
//       Router.push('/minha-conta')
//     }
//   }

//   return { changePassword, loading }
// }

export const setTokens = ({
  access,
  refresh,
}: {
  access: string
  refresh: string
}) => {
  authState.setState({
    zAccessToken: access,
    zRefreshToken: refresh,
  })
}

const isTokenExpired = (token: string | undefined): boolean => {
  if (!token) return true
  const exp = parseJwt(token)?.exp

  if (!exp) return true

  if (Date.now() >= exp * 1000) {
    return true
  }
  return false
}

const setRefreshing = (newIsRefreshing: boolean) => {
  isRefreshing = newIsRefreshing
}

export const getAccessTokenRefreshedIfPossible = async (
  operationName?: string,
): Promise<string | undefined> => {
  const { zAccessToken: accessToken, zRefreshToken: refreshToken } =
    authState.getState()

  const isAuthOperation = operationName?.startsWith('codeAuth')

  // Auth operations dont need token or refresh wait
  if (isAuthOperation) return undefined

  if (isRefreshing) {
    await new Promise((resolve) => setTimeout(resolve, 10))
    return getAccessTokenRefreshedIfPossible(operationName)
  }

  if (!accessToken || !refreshToken) return undefined

  const isAccessTokenExpired = isTokenExpired(accessToken)
  const isRefreshTokenExpired = isTokenExpired(refreshToken)
  // ok access
  if (accessToken && !isAccessTokenExpired) return accessToken
  // both expired
  if (isAccessTokenExpired && isRefreshTokenExpired) {
    logout({})
    throw new Error('Sessão inválida')
    return undefined
  }
  // access expired, refresh
  if (isAccessTokenExpired && refreshToken && !isRefreshTokenExpired) {
    const variables: CodeAuthRefreshTokensMutationVariables = {
      token: refreshToken,
    }
    setRefreshing(true)
    // HERE RETURNS THE ACCESS TOKEN IN HEADER AND REPLACE IN MIDDLEWARE
    await client.mutate({
      mutation: CodeAuthRefreshTokensDocument,
      variables,
    })
    setRefreshing(false)
    return authState.getState().zAccessToken
  }
  // Access valid without refresh?

  return undefined // TODO: redirect to login again (backend requests using this breaks get static props of page)
}

/**
 * Keep user updated at every refresh (if data is old)
 */
export const useAuthAutoUpdater = () => {
  const updater = updateUserAuthMeNotObservable

  async function getMeIfNeeded(force = false): Promise<void> {
    const user = getAuthStateUser()

    if (force) {
      updater()
      return
    }

    if (!user) {
      await new Promise((resolve) => setTimeout(resolve, 2000))
      return getMeIfNeeded()
    }
    const userFetchDate = new Date(user.computedResolvedAt)
    const now = new Date()
    const isOldMoreThan5Minutes =
      now.getTime() - userFetchDate.getTime() > 5 * 1000

    if (isOldMoreThan5Minutes) {
      updater()
    }
  }

  useEffect(() => {
    getMeIfNeeded() // get latest token and update user on every f5
    // getAccessTokenRefreshedIfPossible()
  }, [])

  return () => getMeIfNeeded(true)
}

export const setMaintainerAdminSelectedTeam = (id: number) => {
  authState.setState({ maintainerAdminSelectedTeamId: id })
}

export const getUserMaintainerAdminSelectedTeam = ({
  user,
  maintainerAdminSelectedTeamId,
}: {
  user?: UserFieldsFragment
  maintainerAdminSelectedTeamId?: number
}) => {
  if (!user?.MaintainerAdmin) return undefined
  const allTeams = user.MaintainerAdmin?.Maintainer.Teams
  console.log({ enabled: allTeams })
  if (allTeams?.length === 0) return undefined
  const firstActiveTeam = allTeams.find((t) => t.isActive)?.id
  if (!maintainerAdminSelectedTeamId) return firstActiveTeam

  const allTeamIds = allTeams.map((t) => t.id)
  const has = allTeamIds.includes(maintainerAdminSelectedTeamId)
  console.log({ has, maintainerAdminSelectedTeamId })
  return has ? maintainerAdminSelectedTeamId : firstActiveTeam
}

export const useMaintainerAdminSelectedTeam = () => {
  const user = authState((s) => s.user)
  const maintainerAdminSelectedTeamId = authState(
    (s) => s.maintainerAdminSelectedTeamId,
  )

  return getUserMaintainerAdminSelectedTeam({
    user,
    maintainerAdminSelectedTeamId,
  })
}

export const getMaintainerAdminSelectedTeam = () => {
  return getUserMaintainerAdminSelectedTeam({
    user: authState.getState().user,
    maintainerAdminSelectedTeamId:
      authState.getState().maintainerAdminSelectedTeamId,
  })
}
